import React, { useCallback, useMemo, useRef } from 'react';
// Backend integration fix for Songs layer structured outputs
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import {
  AGE_GROUP_VALUES,
  CULTURAL_CONTEXT_VALUES,
  GENRE_VALUES,
  INTENSITY_LEVEL_VALUES,
  MOOD_VALUES,
  PERFORMANCE_CONTEXT_VALUES,
  type AgeGroup,
  type CulturalContext,
  type Genre,
  type IntensityLevel,
  type Mood,
  type PerformanceContext,
} from '../../contracts/openapi/assetContracts';
import {
  ageGroupLabels,
  culturalContextLabels,
  genreLabels,
  intensityLevelLabels,
  moodLabels,
  performanceContextLabels,
} from '../../contracts/openapi/assetEnumLabels';
import { environmentSafeLog } from '../../utils/environment';
import { SongsLayerMetadata } from '../../types/layerMetadata';
import {
  SONG_FIELD_OPTIONS,
  normalizeTempo,
} from '../../utils/songMetadataUtils';
import { SongMetadataValidator } from '../../utils/songMetadataValidation';
import { API_STRING_MAX_LENGTH, SONG_METADATA } from '../../constants/apiMetadataConstraints';
import { ALGORHYTHM_ENUMS, ALGORHYTHM_LABELS } from '../../types/algorhythm.types';
import {
  buildEnumOptions,
  coerceEnumArray,
  coerceEnumValue,
} from './enumFieldUtils';

interface SongsMetadataEditorProps {
  layerMetadata: SongsLayerMetadata; // Restored layer-agnostic interface
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: SongsLayerMetadata) => void;
  // Legacy support during migration
  asset?: any;
  // PHASE 1 CONSISTENCY FIX: AI-generated description for fallback pattern (matches S, L, M, W layers)
  aiGeneratedDescription?: string;
}

/**
 * Apply intelligent defaults to prevent null/undefined/empty values
 * This ensures all fields have sensible defaults to avoid backend validation errors
 *
 * WHY THIS IS NEEDED:
 * Recent backend changes (likely V2.0 layerMetadata migration or MongoDB validation updates)
 * now reject null/undefined/empty string values that previously worked fine.
 *
 * INVESTIGATION NOTES:
 * - language: "" was specifically causing 500 "language override unsupported" errors
 * - This suggests new validation middleware or schema changes
 * - Defensive programming: provide intelligent defaults for ALL fields
 * - Better UX: users see meaningful defaults instead of empty forms
 */
const applyIntelligentDefaults = (data: any) => {
  const currentYear = new Date().getFullYear();

  // Start with existing data, then apply defaults for any null/undefined values
  const defaults = {
    // Core song information
    songName: 'Untitled Song',
    artistName: 'Unknown Artist',
    albumName: 'Unknown Album',

    // Musical characteristics with sensible defaults (API: SongMetadataRequest only)
    genre: ['POP'],
    mood: ['CALM'],
    tempo: 'Medium',
    energy: '',

    // Technical details
    bpm: 120,
    duration: 180,
    key: 'C Major',
    timeSignature: '4/4',
    releaseYear: currentYear,

    // Additional metadata (API fields)
    albumArt: '',
    albumArtUrl: '',
    instruments: ['vocals'],
    themes: ['general'],
    style: 'contemporary',
    production: 'studio',
    recordingQuality: 'high',
    popularity: 'underground',

    valence: 'neutral',
    acousticness: 'medium',
    instrumentalness: 'low',
    liveness: 'low',
    speechiness: 'low',

    layerType: 'songs',
    richDescription: '',
    comprehensiveTags: [],
  };

  // Apply defaults only for null, undefined values (NOT empty strings - preserve user's choice to clear)
  const result = { ...data };

  Object.keys(defaults).forEach(key => {
    // Don't apply defaults to empty strings - user may have intentionally cleared the field
    if (result[key] === null || result[key] === undefined) {
      result[key] = defaults[key as keyof typeof defaults];
      console.log(`🎯 [INTELLIGENT DEFAULT] ${key}: ${result[key]}`);
    }
  });

  // Special handling for albumArt field (backend provides albumArt, frontend sometimes expects albumArtUrl)
  if (!result.albumArt && result.albumArtUrl) {
    result.albumArt = result.albumArtUrl;
  }
  // Ensure consistent albumArt field availability
  if (result.albumArt && !result.albumArtUrl) {
    result.albumArtUrl = result.albumArt;
  }

  return result;
};

const SongsMetadataEditor: React.FC<SongsMetadataEditorProps> = ({
  layerMetadata,
  asset,
  isEditable = false,
  onMetadataChange,
  aiGeneratedDescription,
}) => {
  // AI CONTRACT: Enhanced priority-based metadata extraction with contract compatibility
  // 🎵 SONGS LAYER FIX: Prioritize layerMetadata prop for new API structure
  const songMetadata = React.useMemo(() => {
    // Priority 0: layerMetadata prop (preferred - new API structure)
    if (layerMetadata) {
      environmentSafeLog(
        '📌 [SONGS AI CONTRACT] Using layerMetadata prop (new API structure)'
      );
      // Handle nested data structure if needed
      const actualMetadata =
        layerMetadata?.songMetadata ||
        layerMetadata?.data?.layerMetadata ||
        layerMetadata;
      return actualMetadata;
    }

    // Handle undefined asset case
    if (!asset) {
      console.log(
        '🚨 [SONGS AI CONTRACT] Asset is undefined, returning empty metadata'
      );
      return null;
    }

    environmentSafeLog(
      '🔎 [SONGS AI CONTRACT] Extracting metadata from asset (layer-agnostic):',
      {
        hasSongMetadata: !!asset?.songMetadata,
        hasLayerMetadata: !!asset?.aiMetadata?.layerMetadata,
        hasLegacySongMetadata: !!asset?.aiMetadata?.songMetadata,
        hasMetadataSongMetadata: !!asset?.metadata?.songMetadata,
      }
    );

    let rawMetadata = null;

    // Priority 1: LAYER-AGNOSTIC AI metadata (preferred for all layers)
    if (asset?.aiMetadata?.layerMetadata) {
      environmentSafeLog(
        '📌 [SONGS AI CONTRACT] Using aiMetadata.layerMetadata (layer-agnostic)'
      );
      rawMetadata = asset.aiMetadata.layerMetadata;
    }
    // Priority 2: Root level songMetadata (legacy support)
    else if (asset?.songMetadata) {
      environmentSafeLog(
        '📌 [SONGS AI CONTRACT] Using root songMetadata (legacy)'
      );
      rawMetadata = asset.songMetadata;
    }
    // Priority 3: AI songMetadata structure (legacy fallback)
    else if (asset?.aiMetadata?.songMetadata) {
      environmentSafeLog(
        '📌 [SONGS AI CONTRACT] Using aiMetadata.songMetadata (legacy)'
      );
      rawMetadata = asset.aiMetadata.songMetadata;
    }
    // Priority 4: Legacy metadata structure
    else if (asset?.metadata?.songMetadata) {
      environmentSafeLog(
        '📌 [SONGS AI CONTRACT] Using metadata.songMetadata (legacy)'
      );
      rawMetadata = asset.metadata.songMetadata;
    } else {
      environmentSafeLog(
        '⚠️ [SONGS AI CONTRACT] No song metadata found, using empty object'
      );
      rawMetadata = {};
    }

    // Apply nested data access pattern for backward compatibility
    const actualSongMetadata =
      rawMetadata?.songMetadata ||
      rawMetadata?.data?.songMetadata ||
      rawMetadata;
    environmentSafeLog(
      '🔄 [SONGS AI CONTRACT] Final metadata after nested access:',
      actualSongMetadata
    );

    return actualSongMetadata;
  }, [layerMetadata, asset]);

  // Helper functions for duration format conversion - MOVED BEFORE useState to fix hoisting issue
  const convertDurationToSeconds = (duration: string | number): number => {
    if (typeof duration === 'number') return duration;
    if (typeof duration === 'string') {
      // Handle MM:SS format like "3:30"
      if (duration.includes(':')) {
        const parts = duration.split(':');
        if (parts.length === 2) {
          const minutes = parseInt(parts[0], 10) || 0;
          const seconds = parseInt(parts[1], 10) || 0;
          return minutes * 60 + seconds;
        }
      }
      // Handle plain number strings
      const numValue = parseInt(duration, 10);
      return isNaN(numValue) ? 0 : numValue;
    }
    return 0;
  };

  const convertSecondsToDisplay = (seconds: number): string => {
    if (!seconds || seconds === 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // State for editable fields - initialize with enhanced normalization
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [SONGS INFO INIT] Initializing editableData state with songMetadata:',
      songMetadata
    );

    // Apply comprehensive normalization to all fields
    const rawData = {
      // Creator Input (CRITICAL for AI)
      // Auto-populate with AI description if creator hasn't provided one yet
      creatorDescription: songMetadata?.creatorDescription ||
                         songMetadata?.richDescription ||
                         songMetadata?.description ||
                         songMetadata?.aiDescription ||
                         asset?.aiMetadata?.description || '',

      songName:
        songMetadata?.songName ||
        songMetadata?.songTitle ||
        songMetadata?.songsMetadata?.songName ||
        '',
      artistName:
        songMetadata?.artistName ||
        songMetadata?.songsMetadata?.artistName ||
        '',
      albumName:
        songMetadata?.albumName || songMetadata?.songsMetadata?.albumName || '',
      genre: coerceEnumArray(songMetadata?.genre, GENRE_VALUES, SONG_METADATA.genre.maxItems),
      mood: coerceEnumArray(songMetadata?.mood, MOOD_VALUES, SONG_METADATA.mood.maxItems),
      tempo: songMetadata?.tempo || 'Medium',
      energy: coerceEnumValue(songMetadata?.energy, INTENSITY_LEVEL_VALUES),
      bpm: songMetadata?.bpm || '',
      duration: convertDurationToSeconds(songMetadata?.duration || ''),
      key: songMetadata?.key || '',
      timeSignature: songMetadata?.timeSignature || '',
      releaseYear: songMetadata?.releaseYear || '',
      richDescription: songMetadata?.richDescription ||
                      songMetadata?.description ||
                      songMetadata?.aiDescription ||
                      asset?.aiMetadata?.description || '',
      comprehensiveTags: Array.isArray(songMetadata?.comprehensiveTags)
        ? songMetadata?.comprehensiveTags
        : typeof songMetadata?.comprehensiveTags === 'string' &&
          songMetadata?.comprehensiveTags
        ? songMetadata.comprehensiveTags.split(',').map((t: string) => t.trim())
        : [],

      // Album art URL field for editing - use both field names for compatibility
      albumArt: songMetadata?.albumArt ||
                songMetadata?.albumArtUrl ||
                songMetadata?.artworkUrl ||
                asset?.albumArt || '',
      albumArtUrl: songMetadata?.albumArtUrl ||
                   songMetadata?.albumArt ||
                   songMetadata?.artworkUrl ||
                   asset?.albumArt || '',

      // AlgoRhythm Metadata (REQUIRED for recommendation engine)
      // 🔧 CRITICAL FIX: Read from root-level asset.algorhythmMetadata (persisted user edits)
      // WORKAROUND: Also check songMetadata.algorhythmMetadata (backend not extracting to root during creation)
      performanceContext: Array.isArray(asset?.algorhythmMetadata?.performanceContext)
        ? coerceEnumArray(asset.algorhythmMetadata.performanceContext, PERFORMANCE_CONTEXT_VALUES, 50)
        : Array.isArray((songMetadata as any)?.algorhythmMetadata?.performanceContext)
        ? coerceEnumArray((songMetadata as any).algorhythmMetadata.performanceContext, PERFORMANCE_CONTEXT_VALUES, 50)
        : Array.isArray(songMetadata?.performanceContext)
        ? coerceEnumArray(songMetadata.performanceContext, PERFORMANCE_CONTEXT_VALUES, 50)
        : [],
      targetAudience: Array.isArray(asset?.algorhythmMetadata?.targetAudience)
        ? coerceEnumArray(asset.algorhythmMetadata.targetAudience, AGE_GROUP_VALUES, 50)
        : Array.isArray((songMetadata as any)?.algorhythmMetadata?.targetAudience)
        ? coerceEnumArray((songMetadata as any).algorhythmMetadata.targetAudience, AGE_GROUP_VALUES, 50)
        : Array.isArray(songMetadata?.targetAudience)
        ? coerceEnumArray(songMetadata.targetAudience, AGE_GROUP_VALUES, 50)
        : [],
      ageAppropriateness: Array.isArray(asset?.algorhythmMetadata?.ageAppropriateness)
        ? asset.algorhythmMetadata.ageAppropriateness
        : Array.isArray((songMetadata as any)?.algorhythmMetadata?.ageAppropriateness)
        ? (songMetadata as any).algorhythmMetadata.ageAppropriateness
        : Array.isArray(songMetadata?.ageAppropriateness)
        ? songMetadata.ageAppropriateness
        : typeof songMetadata?.ageAppropriateness === 'string' && songMetadata?.ageAppropriateness
        ? songMetadata.ageAppropriateness.split(',').map((s: string) => s.trim())
        : [],
      culturalContext: Array.isArray(asset?.algorhythmMetadata?.culturalContext)
        ? coerceEnumArray(asset.algorhythmMetadata.culturalContext, CULTURAL_CONTEXT_VALUES, 50)
        : Array.isArray((songMetadata as any)?.algorhythmMetadata?.culturalContext)
        ? coerceEnumArray((songMetadata as any).algorhythmMetadata.culturalContext, CULTURAL_CONTEXT_VALUES, 50)
        : Array.isArray(songMetadata?.culturalContext)
        ? coerceEnumArray(songMetadata.culturalContext, CULTURAL_CONTEXT_VALUES, 50)
        : [],
      musicalStyle: Array.isArray(asset?.algorhythmMetadata?.musicalStyle)
        ? asset.algorhythmMetadata.musicalStyle
        : Array.isArray((songMetadata as any)?.algorhythmMetadata?.musicalStyle)
        ? (songMetadata as any).algorhythmMetadata.musicalStyle
        : Array.isArray(songMetadata?.musicalStyle)
        ? songMetadata.musicalStyle
        : typeof songMetadata?.musicalStyle === 'string' && songMetadata?.musicalStyle
        ? songMetadata.musicalStyle.split(',').map((s: string) => s.trim())
        : [],
    };

    // Apply enhanced normalization; genre stored as array for multi-select
    return {
      ...rawData,
      genre: rawData.genre,
      mood: rawData.mood,
      energy: rawData.energy,
      tempo: normalizeTempo(rawData.tempo),
    };
  });

  // Track metadata hash to prevent infinite update loops
  const metadataHashRef = useRef<string>('');
  const computedHash = JSON.stringify(songMetadata);

  // Update editableData when songMetadata changes (from AI generation)
  React.useEffect(() => {
    if (computedHash !== metadataHashRef.current) {
      console.log(
        '🔄 [SONGS INFO] songMetadata changed, updating editableData'
      );
      metadataHashRef.current = computedHash;

      // Apply enhanced normalization to updated metadata
      const rawData = {
        songName:
          songMetadata?.songName ||
          songMetadata?.songTitle ||
          songMetadata?.songsMetadata?.songName ||
          '',
        artistName:
          songMetadata?.artistName ||
          songMetadata?.songsMetadata?.artistName ||
          '',
        albumName:
          songMetadata?.albumName ||
          songMetadata?.songsMetadata?.albumName ||
          '',
        genre: coerceEnumArray(songMetadata?.genre, GENRE_VALUES, SONG_METADATA.genre.maxItems),
        mood: coerceEnumArray(songMetadata?.mood, MOOD_VALUES, SONG_METADATA.mood.maxItems),
        tempo: songMetadata?.tempo || 'Medium',
        energy: coerceEnumValue(songMetadata?.energy, INTENSITY_LEVEL_VALUES),
        bpm: songMetadata?.bpm || '',
        duration: convertDurationToSeconds(songMetadata?.duration || ''),
        key: songMetadata?.key || '',
        timeSignature: songMetadata?.timeSignature || '',
        releaseYear: songMetadata?.releaseYear || '',
        richDescription: songMetadata?.richDescription ||
                        songMetadata?.description ||
                        songMetadata?.aiDescription ||
                        asset?.aiMetadata?.description || '',
        comprehensiveTags: Array.isArray(songMetadata?.comprehensiveTags)
          ? songMetadata?.comprehensiveTags
          : typeof songMetadata?.comprehensiveTags === 'string' &&
            songMetadata?.comprehensiveTags
          ? songMetadata.comprehensiveTags
              .split(',')
              .map((t: string) => t.trim())
          : [],

        // Album art URL field for editing - use both field names for compatibility
        albumArt: songMetadata?.albumArt ||
                  songMetadata?.albumArtUrl ||
                  songMetadata?.artworkUrl ||
                  asset?.albumArt || '',
        albumArtUrl: songMetadata?.albumArtUrl ||
                     songMetadata?.albumArt ||
                     songMetadata?.artworkUrl ||
                     asset?.albumArt || '',

        // Creator description - Auto-populate with AI description if not provided
        creatorDescription: songMetadata?.creatorDescription ||
                           editableData?.creatorDescription ||
                           songMetadata?.richDescription ||
                           songMetadata?.description ||
                           songMetadata?.aiDescription ||
                           asset?.aiMetadata?.description || '',

        // AlgoRhythm Metadata (REQUIRED for recommendation engine)
        performanceContext: Array.isArray(songMetadata?.performanceContext)
          ? coerceEnumArray(songMetadata?.performanceContext, PERFORMANCE_CONTEXT_VALUES, 50)
          : editableData?.performanceContext || [],
        targetAudience: Array.isArray(songMetadata?.targetAudience)
          ? coerceEnumArray(songMetadata?.targetAudience, AGE_GROUP_VALUES, 50)
          : editableData?.targetAudience || [],
        ageAppropriateness: Array.isArray(songMetadata?.ageAppropriateness)
          ? songMetadata?.ageAppropriateness
          : typeof songMetadata?.ageAppropriateness === 'string' && songMetadata?.ageAppropriateness
          ? songMetadata.ageAppropriateness.split(',').map((s: string) => s.trim())
          : editableData?.ageAppropriateness || [],
        culturalContext: Array.isArray(songMetadata?.culturalContext)
          ? coerceEnumArray(songMetadata?.culturalContext, CULTURAL_CONTEXT_VALUES, 50)
          : editableData?.culturalContext || [],
        musicalStyle: Array.isArray(songMetadata?.musicalStyle)
          ? songMetadata?.musicalStyle
          : typeof songMetadata?.musicalStyle === 'string' && songMetadata?.musicalStyle
          ? songMetadata.musicalStyle.split(',').map((s: string) => s.trim())
          : editableData?.musicalStyle || [],
      };

      const finalData = {
        ...rawData,
        genre: rawData.genre,
        mood: rawData.mood,
        energy: rawData.energy,
        tempo: normalizeTempo(rawData.tempo),
      };

      setEditableData(finalData);
    }
  }, [computedHash, songMetadata]);

  React.useEffect(() => {
    SongMetadataValidator.validateSongMetadata(editableData);
  }, [editableData]);

  // Album art preview: effective URL and error state (simple img, no AssetThumbnail)
  const [albumArtError, setAlbumArtError] = React.useState(false);
  const effectiveAlbumArtUrl = React.useMemo(() => {
    let url = isEditable
      ? editableData.albumArtUrl || ''
      : songMetadata?.albumArt ||
        songMetadata?.artworkUrl ||
        asset?.albumArt ||
        '';
    if (url && url.includes('.webp')) {
      url = url.replace(/\.webp/g, '.jpg');
    }
    return url || '';
  }, [
    isEditable,
    editableData.albumArtUrl,
    songMetadata?.albumArt,
    songMetadata?.artworkUrl,
    asset?.albumArt,
  ]);
  React.useEffect(() => {
    setAlbumArtError(false);
  }, [effectiveAlbumArtUrl]);

  // Debounce timeouts for field changes
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Enhanced field change handler with debouncing (following LooksInformation pattern)
  const handleFieldChange = useCallback(
    (field: string, value: string | number | boolean | string[]) => {
      setEditableData(prev => ({ ...prev, [field]: value }));

      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            // Create a copy for backend processing (separate from form display data)
            const backendData: any = {
              ...layerMetadata, // Start with all required V2.0 fields
              ...current, // Override with edited values
            };

            // Type conversion for specific fields
            if (field === 'bpm' && typeof backendData.bpm === 'string') {
              backendData.bpm = backendData.bpm
                ? parseInt(backendData.bpm, 10)
                : '';
            }
            if (
              field === 'duration' &&
              typeof backendData.duration === 'string'
            ) {
              backendData.duration = backendData.duration
                ? parseInt(backendData.duration, 10)
                : 0;
            }
            if (
              field === 'releaseYear' &&
              typeof backendData.releaseYear === 'string'
            ) {
              backendData.releaseYear = backendData.releaseYear
                ? parseInt(backendData.releaseYear, 10)
                : '';
            }

            // BACKEND ALIGNMENT: Convert string fields to arrays per backend requirements

            // Genre field - now expects array; allow [] so validation can fail when required
            backendData.genre = coerceEnumArray(
              backendData.genre,
              GENRE_VALUES,
              SONG_METADATA.genre.maxItems
            );
            backendData.mood = coerceEnumArray(
              backendData.mood,
              MOOD_VALUES,
              SONG_METADATA.mood.maxItems
            );
            backendData.energy = coerceEnumValue(
              backendData.energy,
              INTENSITY_LEVEL_VALUES
            );
            backendData.performanceContext = coerceEnumArray(
              backendData.performanceContext,
              PERFORMANCE_CONTEXT_VALUES,
              50
            );
            backendData.targetAudience = coerceEnumArray(
              backendData.targetAudience,
              AGE_GROUP_VALUES,
              50
            );
            backendData.culturalContext = coerceEnumArray(
              backendData.culturalContext,
              CULTURAL_CONTEXT_VALUES,
              50
            );

            // songCulturalOrigin field - new field, expects array
            if (!backendData.songCulturalOrigin) {
              backendData.songCulturalOrigin = []; // Initialize as empty array
              console.log('🌎 [CULTURAL ORIGIN] Initialized as empty array');
            } else if (typeof backendData.songCulturalOrigin === 'string') {
              backendData.songCulturalOrigin = backendData.songCulturalOrigin
                .split(',')
                .map((c: string) => c.trim())
                .filter(Boolean);
              console.log(
                '🌎 [CULTURAL ORIGIN FIX] String → array:',
                backendData.songCulturalOrigin
              );
            }

            // Energy field - handle database corruption (number vs string) only; allow '' so validation can block next
            // Handle other corrupted numeric fields that should be strings
            const numericToStringFields = [
              'danceability',
              'acousticness',
              'instrumentalness',
              'liveness',
              'speechiness',
            ];
            const valueMap = { 1: 'Low', 2: 'Medium', 3: 'High' };

            numericToStringFields.forEach(field => {
              if (typeof backendData[field] === 'number') {
                backendData[field] =
                  valueMap[backendData[field] as keyof typeof valueMap] ||
                  'Medium';
                console.log(
                  `🔧 [${field.toUpperCase()} FIX] Database corruption - number → string:`,
                  backendData[field]
                );
              }
            });

            if (
              field === 'ageAppropriateness' &&
              Array.isArray(backendData.ageAppropriateness)
            ) {
              // CRITICAL FIX: Filter out character-split values, normalize duplicates - keep as array
              backendData.ageAppropriateness = Array.from(
                new Set(
                  backendData.ageAppropriateness
                    .filter(
                      (age: any) => typeof age === 'string' && age.length > 1
                    ) // Remove single characters
                    .map((age: string) => age.replace(/-/g, ' ').trim()) // Normalize "all-ages" → "all ages"
                )
              );
            }

            // COMPREHENSIVE INTELLIGENT DEFAULTS: Ensure no null/undefined values
            const finalBackendData = applyIntelligentDefaults(backendData);
            console.log(
              '🎯 [INTELLIGENT DEFAULTS] Applied defaults to prevent null values:',
              {
                original: Object.keys(backendData).length,
                final: Object.keys(finalBackendData).length,
                field: field,
              }
            );

            onMetadataChange(finalBackendData);
            return current;
          });
        }
      }, 500);
    },
    [onMetadataChange]
  );

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Memoized dropdown options to prevent unnecessary re-renders
  const genreOptions = useMemo(
    () => buildEnumOptions(GENRE_VALUES, genreLabels),
    []
  );

  const moodOptions = useMemo(
    () => buildEnumOptions(MOOD_VALUES, moodLabels),
    []
  );

  const energyOptions = useMemo(
    () => buildEnumOptions(INTENSITY_LEVEL_VALUES, intensityLevelLabels),
    []
  );

  const performanceContextOptions = useMemo(
    () => buildEnumOptions(PERFORMANCE_CONTEXT_VALUES, performanceContextLabels),
    []
  );

  const targetAudienceOptions = useMemo(
    () => buildEnumOptions(AGE_GROUP_VALUES, ageGroupLabels),
    []
  );

  const culturalContextOptions = useMemo(
    () => buildEnumOptions(CULTURAL_CONTEXT_VALUES, culturalContextLabels),
    []
  );

  const tempoOptions = useMemo(
    () => SONG_FIELD_OPTIONS.tempos,
    []
  );

  // Always render for G layer assets to allow metadata editing
  // Even if initial metadata is missing, users should be able to add it
  // Fix: Render if layerMetadata is provided OR asset has correct layer
  if (!layerMetadata && (!asset || asset.layer !== 'G')) {
    return null;
  }

  return (
    <Box sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <MusicNoteIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Song Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Core Song Information with Album Art */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Core Information
          </Typography>
        </Grid>

        {/* Create a container for the 2-column layout */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {/* Column 1: Core Info Fields - Increased width for better spacing */}
            <Box sx={{ flex: 1, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, maxWidth: '75%' }}>
              {/* Song Name */}
              <Box>
                {isEditable ? (
                  <TextField
                    required
                    fullWidth
                    label="Song Name"
                    value={editableData.songName}
                    onChange={e => handleFieldChange('songName', e.target.value)}
                    size="small"
                    inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
                    helperText={(!editableData.songName || editableData.songName.trim() === '' ? 'Song name is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
                    error={!editableData.songName || editableData.songName.trim() === ''}
                  />
                ) : (
                  <Typography variant="body2">
                    <strong>Song Name:</strong>{' '}
                    {editableData.songName || 'Not specified'}
                  </Typography>
                )}
              </Box>

              {/* Artist Name */}
              <Box>
                {isEditable ? (
                  <TextField
                    required
                    fullWidth
                    label="Artist Name"
                    value={editableData.artistName}
                    onChange={e => handleFieldChange('artistName', e.target.value)}
                    size="small"
                    inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
                    helperText={(!editableData.artistName || editableData.artistName.trim() === '' ? 'Artist name is required. ' : '') + `${API_STRING_MAX_LENGTH} characters max`}
                    error={!editableData.artistName || editableData.artistName.trim() === ''}
                  />
                ) : (
                  <Typography variant="body2">
                    <strong>Artist Name:</strong>{' '}
                    {editableData.artistName || 'Not specified'}
                  </Typography>
                )}
              </Box>

              {/* Album Name */}
              <Box>
                {isEditable ? (
                  <TextField
                    fullWidth
                    label="Album Name"
                    value={editableData.albumName}
                    onChange={e => handleFieldChange('albumName', e.target.value)}
                    size="small"
                    inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
                    helperText={`${API_STRING_MAX_LENGTH} characters max`}
                  />
                ) : (
                  <Typography variant="body2">
                    <strong>Album Name:</strong>{' '}
                    {editableData.albumName || 'Not specified'}
                  </Typography>
                )}
              </Box>

              {/* Album Art URL - New 5th field spanning full width */}
              <Box sx={{ gridColumn: '1 / -1' }}>
                {(() => {
                  // For editing, ONLY use editableData.albumArtUrl to prevent sticky behavior
                  const albumArtUrl = isEditable
                    ? (editableData.albumArtUrl || '')
                    : (songMetadata?.albumArt ||
                       songMetadata?.artworkUrl ||
                       asset?.albumArt);

                  return isEditable ? (
                    <TextField
                      fullWidth
                      label="Album Art URL"
                      value={albumArtUrl || ''}
                      inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
                      onChange={e => {
                        // Auto-convert .webp URLs to .jpg for better compatibility
                        let newUrl = e.target.value;

                        // Clean URL: remove trailing whitespace, newlines, and other invalid characters
                        newUrl = newUrl.trim().replace(/[\n\r\t]/g, '');

                        // Handle both direct .webp endings and Apple Music URLs with .webp extensions
                        if (newUrl.includes('.webp')) {
                          // For Apple Music URLs like: /632x632bb.webp -> /632x632bb.jpg
                          newUrl = newUrl.replace(/\.webp/g, '.jpg');
                          // Auto-converted .webp to .jpg for better compatibility
                        }

                        // Update albumArtUrl and clear albumArt to prevent conflicts
                        handleFieldChange('albumArtUrl', newUrl);
                        if (newUrl !== editableData.albumArt) {
                          // Clear albumArt when user edits albumArtUrl to prevent sticky behavior
                          setEditableData(prev => ({ ...prev, albumArt: '' }));
                        }
                      }}
                      size="small"
                      multiline
                      minRows={2}
                      placeholder="Enter Album Art image URL"
                      helperText="Provide a direct URL to the album art image"
                    />
                  ) : (
                    <Typography variant="body2">
                      <strong>Album Art URL:</strong>{' '}
                      {albumArtUrl ? (
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            wordBreak: 'break-all',
                            bgcolor: 'grey.100',
                            p: 0.5,
                            borderRadius: 0.5,
                            display: 'block',
                            mt: 0.5
                          }}
                        >
                          {albumArtUrl}
                        </Typography>
                      ) : (
                        'Not specified'
                      )}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>

            {/* Column 2: Album Art preview (simple img from URL, no AssetThumbnail) */}
            {effectiveAlbumArtUrl && (
              <Box sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.25,
                    color: 'primary.main',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    mt: -1.5,
                  }}
                >
                  Album Art
                </Typography>
                <Box
                  sx={{
                    width: 165,
                    height: 165,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 3,
                    border: '2px solid',
                    borderColor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.100',
                  }}
                >
                  {albumArtError ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        p: 1,
                      }}
                    >
                      <ImageNotSupportedIcon
                        sx={{ fontSize: 48, color: 'grey.400' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', textAlign: 'center' }}
                      >
                        Image unavailable
                      </Typography>
                    </Box>
                  ) : (
                    <img
                      key={effectiveAlbumArtUrl}
                      src={effectiveAlbumArtUrl}
                      alt="Album art"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                      onError={() => setAlbumArtError(true)}
                      onLoad={() => setAlbumArtError(false)}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Musical Characteristics */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Musical Characteristics
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              multiple
              options={genreOptions}
              value={genreOptions.filter(option =>
                editableData.genre.includes(option.value)
              )}
              onChange={(event, newValue) =>
                handleFieldChange(
                  'genre',
                  newValue
                    .map(option => option.value)
                    .slice(0, SONG_METADATA.genre.maxItems)
                )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Genre"
                  required
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  error={!editableData.genre?.length}
                  helperText={
                    !editableData.genre?.length
                      ? 'Genre is required (1–50 items)'
                      : (editableData.genre?.length >= SONG_METADATA.genre.maxItems ? `${SONG_METADATA.genre.maxItems} items max. ` : '') + `${API_STRING_MAX_LENGTH} characters per item`
                  }
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Genre:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {editableData.genre.map((item: Genre, index: number) => (
                  <Chip key={index} label={genreLabels[item]} size="small" variant="outlined" />
                ))}
                {(!editableData.genre?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              multiple
              options={moodOptions}
              value={moodOptions.filter(option =>
                editableData.mood.includes(option.value)
              )}
              onChange={(event, newValue) =>
                handleFieldChange(
                  'mood',
                  newValue.map(option => option.value).slice(0, SONG_METADATA.mood.maxItems)
                )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Mood"
                  required
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  error={!editableData.mood.length}
                  helperText={!editableData.mood.length ? 'Mood is required (1-3 items)' : 'Up to 3 values'}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Mood:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {editableData.mood.map((item: Mood, index: number) => (
                  <Chip key={index} label={moodLabels[item]} size="small" variant="outlined" />
                ))}
                {!editableData.mood.length && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={energyOptions}
              value={energyOptions.find(option => option.value === editableData.energy) ?? null}
              onChange={(event, newValue) => handleFieldChange('energy', newValue?.value || '')}
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Energy Level" 
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Energy:</strong> {editableData.energy ? intensityLevelLabels[editableData.energy as IntensityLevel] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={tempoOptions}
              value={editableData.tempo}
              inputValue={editableData.tempo}
              onInputChange={(event, newValue) =>
                handleFieldChange('tempo', newValue || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Tempo"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`${API_STRING_MAX_LENGTH} characters max`}
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Tempo:</strong> {editableData.tempo || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Technical Details */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Technical Details
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <TextField
              fullWidth
              label="BPM"
              type="number"
              value={editableData.bpm}
              onChange={e => {
                const value = e.target.value;
                const numValue = parseInt(value, 10);

                // Real-time validation feedback
                if (value && (numValue < 60 || numValue > 200)) {
                  e.target.setCustomValidity('BPM must be between 60 and 200');
                } else {
                  e.target.setCustomValidity('');
                }

                handleFieldChange('bpm', value);
              }}
              error={editableData.bpm && (parseInt(editableData.bpm as string, 10) < 60 || parseInt(editableData.bpm as string, 10) > 200)}
              helperText={
                editableData.bpm && (parseInt(editableData.bpm as string, 10) < 60 || parseInt(editableData.bpm as string, 10) > 200)
                  ? 'BPM should be between 60-200 beats per minute'
                  : 'Beats per minute (60-200 typical range)'
              }
              size="small"
              inputProps={{ min: 60, max: 200 }}
            />
          ) : (
            <Typography variant="body2">
              <strong>BPM:</strong> {editableData.bpm || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Duration (seconds)"
              type="number"
              value={editableData.duration}
              onChange={e =>
                handleFieldChange('duration', parseInt(e.target.value, 10) || 0)
              }
              size="small"
              helperText={
                editableData.duration
                  ? convertSecondsToDisplay(editableData.duration)
                  : ''
              }
            />
          ) : (
            <Typography variant="body2">
              <strong>Duration:</strong>{' '}
              {editableData.duration
                ? convertSecondsToDisplay(editableData.duration)
                : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Key"
              value={editableData.key}
              onChange={e => handleFieldChange('key', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
              placeholder="e.g., C major, A minor"
            />
          ) : (
            <Typography variant="body2">
              <strong>Key:</strong> {editableData.key || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Time Signature"
              value={editableData.timeSignature}
              onChange={e => handleFieldChange('timeSignature', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
              placeholder="e.g., 4/4, 3/4"
            />
          ) : (
            <Typography variant="body2">
              <strong>Time Signature:</strong>{' '}
              {editableData.timeSignature || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Release Year"
              type="number"
              value={editableData.releaseYear}
              onChange={e => handleFieldChange('releaseYear', e.target.value)}
              size="small"
            />
          ) : (
            <Typography variant="body2">
              <strong>Release Year:</strong>{' '}
              {editableData.releaseYear || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* ========== ALGORHYTHM METADATA SECTION ========== */}
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ p: 3, backgroundColor: 'background.paper', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
              🎯 AlgoRhythm Metadata
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Required for AI-powered recommendations and cross-layer matching
            </Typography>

            <Grid container spacing={2}>
              {/* Performance Context */}
              <Grid item xs={12}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    fullWidth
                    options={performanceContextOptions}
                    value={performanceContextOptions.filter(option =>
                      (editableData.performanceContext || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      handleFieldChange(
                        'performanceContext',
                        newValue.map(option => option.value)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Performance Context *"
                        placeholder="Select 2-4 contexts"
                        helperText="Where is this song typically performed or played? (Select 2-4)"
                        error={!editableData.performanceContext || editableData.performanceContext.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Performance Context:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.performanceContext || []).map((item: PerformanceContext, index: number) => (
                        <Chip key={index} label={performanceContextLabels[item]} size="small" variant="outlined" />
                      ))}
                      {(!editableData.performanceContext || editableData.performanceContext.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Target Audience */}
              <Grid item xs={12}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    fullWidth
                    options={targetAudienceOptions}
                    value={targetAudienceOptions.filter(option =>
                      (editableData.targetAudience || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      handleFieldChange(
                        'targetAudience',
                        newValue.map(option => option.value)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target Audience *"
                        placeholder="Select 1-3 audiences"
                        helperText="Who is this song intended for? (Select 1-3)"
                        error={!editableData.targetAudience || editableData.targetAudience.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Target Audience:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.targetAudience || []).map((item: AgeGroup, index: number) => (
                        <Chip key={index} label={ageGroupLabels[item]} size="small" variant="outlined" />
                      ))}
                      {(!editableData.targetAudience || editableData.targetAudience.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Age Appropriateness */}
              <Grid item xs={12}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    fullWidth
                    options={[
                      'All Ages',
                      'Children',
                      'Teens',
                      'Young Adults',
                      'Adults',
                      'Mature Adults',
                      'Families',
                    ]}
                    value={editableData.ageAppropriateness || []}
                    onChange={(event, newValue) => handleFieldChange('ageAppropriateness', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Age Appropriateness *"
                        placeholder="Select 1-3 age ranges"
                        helperText="What age groups is this song appropriate for? (Select 1-3)"
                        error={!editableData.ageAppropriateness || editableData.ageAppropriateness.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Age Appropriateness:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.ageAppropriateness || []).map((item: string, index: number) => (
                        <Chip key={index} label={item} size="small" variant="outlined" />
                      ))}
                      {(!editableData.ageAppropriateness || editableData.ageAppropriateness.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Cultural Context */}
              <Grid item xs={12}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    fullWidth
                    options={culturalContextOptions}
                    value={culturalContextOptions.filter(option =>
                      (editableData.culturalContext || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      handleFieldChange(
                        'culturalContext',
                        newValue.map(option => option.value)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cultural Context *"
                        placeholder="Select 1-3 contexts"
                        helperText="What cultural influences does this song have? (Select 1-3)"
                        error={!editableData.culturalContext || editableData.culturalContext.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Cultural Context:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.culturalContext || []).map((item: CulturalContext, index: number) => (
                        <Chip key={index} label={culturalContextLabels[item]} size="small" variant="outlined" />
                      ))}
                      {(!editableData.culturalContext || editableData.culturalContext.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Musical Style */}
              <Grid item xs={12}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    fullWidth
                    options={[...ALGORHYTHM_ENUMS.musicalStyle]}
                    getOptionLabel={(option) =>
                      ALGORHYTHM_LABELS.musicalStyle[option as keyof typeof ALGORHYTHM_LABELS.musicalStyle] ?? option
                    }
                    value={editableData.musicalStyle || []}
                    onChange={(event, newValue) => handleFieldChange('musicalStyle', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Musical Style *"
                        placeholder="Select 1-3 styles"
                        helperText="What musical style(s) best describe this song? (Select 1-3)"
                        error={!editableData.musicalStyle || editableData.musicalStyle.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={ALGORHYTHM_LABELS.musicalStyle[option as keyof typeof ALGORHYTHM_LABELS.musicalStyle] ?? option}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Musical Style:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.musicalStyle || []).map((item: string, index: number) => (
                        <Chip
                          key={index}
                          label={ALGORHYTHM_LABELS.musicalStyle[item as keyof typeof ALGORHYTHM_LABELS.musicalStyle] ?? item}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {(!editableData.musicalStyle || editableData.musicalStyle.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Info Alert */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: 'info.light', borderRadius: 1, mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    💡 Why These Fields Matter
                  </Typography>
                  <Typography variant="caption">
                    AlgoRhythm uses these fields to recommend compatible Stars, Looks, Moves, and Worlds for your song.
                    More accurate metadata = better recommendations!
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
};

export default SongsMetadataEditor;
